import 'react-app-polyfill/stable';
import 'core-js';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { persistor, store } from './store/index';
import './styles/index.css';
import './services/localization/Localization';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Anchored origin pattern for the Foodamigos API, so `sentry-trace`/`baggage` are only
 * attached to our own backend and never to third-party hosts.
 */
function apiOriginPatterns(baseUrls) {
  return baseUrls.flatMap((baseUrl) => {
    try {
      const { origin } = new URL(baseUrl);

      return [new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(/|$)`)];
    } catch {
      return [];
    }
  });
}

/**
 * Parses the trace sample rate from env; anything that is not a number in [0, 1] (empty,
 * a typo, a percentage) falls back to the default instead of silently disabling tracing.
 */
function traceSampleRate(value, fallback = 0.05) {
  const rate = parseFloat(value);

  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : fallback;
}

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    normalizeDepth: 10,
    // Lower-case: the Sentry alert rules filter on `environment:production`.
    environment: import.meta.env.VITE_MODE?.toLowerCase(),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: traceSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    tracePropagationTargets: apiOriginPatterns([import.meta.env.VITE_API_BASE_URL]),
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <Provider store={store}>
    <PersistGate persistor={persistor}>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </PersistGate>
  </Provider>,
);

serviceWorkerRegistration.unregister();
